import React, { useCallback, useRef, useState } from "react";
import Cropper from "react-easy-crop";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { Upload, Crop as CropIcon, X, Image as ImgIcon } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

/**
 * Cropped image uploader for admin. User picks a local file → crops to a fixed aspect ratio (default 16:9)
 * → uploads to Emergent Object Store via POST /api/admin/upload/image → returns a same-origin URL like
 * "/api/files/{path}" which is safe to persist in school.image_url.
 *
 * Props:
 *   value        current image URL (string). Preview shows this if provided.
 *   onChange     (url: string) => void  — called with the new URL after successful upload.
 *   aspect       number (default 16/9)
 *   label        header label shown above the drop zone
 *   testidPrefix string for data-testid attrs
 */
export default function ImageCropUploader({ value = "", onChange, aspect = 16 / 9, label = "Foto utama sekolah", testidPrefix = "image-crop" }) {
  const inputRef = useRef(null);
  const [rawSrc, setRawSrc] = useState(null); // dataURL of picked file
  const [open, setOpen] = useState(false);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [busy, setBusy] = useState(false);

  const onCropComplete = useCallback((_, pixels) => setCroppedAreaPixels(pixels), []);

  const pickFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!/^image\/(jpeg|png|webp)$/.test(f.type)) { toast.error("Gunakan JPEG / PNG / WebP"); return; }
    const reader = new FileReader();
    reader.onload = () => { setRawSrc(reader.result); setOpen(true); setCrop({ x: 0, y: 0 }); setZoom(1); };
    reader.readAsDataURL(f);
    e.target.value = ""; // allow re-picking same file
  };

  const cropAndUpload = async () => {
    if (!rawSrc || !croppedAreaPixels) return;
    try {
      setBusy(true);
      const blob = await cropImageToBlob(rawSrc, croppedAreaPixels);
      const fd = new FormData();
      fd.append("file", new File([blob], "school-thumb.jpg", { type: "image/jpeg" }));
      const r = await api.post("/admin/upload/image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange?.(r.data.url);
      toast.success("Foto diunggah");
      setOpen(false);
      setRawSrc(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Gagal mengunggah");
    } finally {
      setBusy(false);
    }
  };

  const clearImage = () => onChange?.("");

  return (
    <div data-testid={`${testidPrefix}-wrapper`}>
      <div className="text-xs uppercase tracking-widest text-[#4B5563] mb-2 flex items-center gap-1.5"><CropIcon size={11} /> {label} <span className="text-[10px] normal-case tracking-normal opacity-70">(dipotong otomatis {aspect === 16/9 ? "16:9" : ""})</span></div>
      {value ? (
        <div className="relative rounded-2xl overflow-hidden border border-[#EAE7E0] bg-[#F3F1ED]" data-testid={`${testidPrefix}-preview`}>
          <img src={value} alt="Thumbnail sekolah" className="w-full block" style={{ aspectRatio: `${aspect}` }} />
          <div className="absolute top-2 right-2 flex gap-1.5">
            <button type="button" onClick={() => inputRef.current?.click()} data-testid={`${testidPrefix}-replace`}
              className="px-3 h-8 rounded-full bg-white/95 hover:bg-white text-[#1E3A8A] text-xs font-medium inline-flex items-center gap-1 shadow"><Upload size={12} /> Ganti</button>
            <button type="button" onClick={clearImage} data-testid={`${testidPrefix}-remove`}
              className="w-8 h-8 rounded-full bg-white/95 hover:bg-white text-[#B91C1C] inline-flex items-center justify-center shadow" title="Hapus"><X size={13} /></button>
          </div>
        </div>
      ) : (
        <button type="button" onClick={() => inputRef.current?.click()} data-testid={`${testidPrefix}-empty-picker`}
          className="w-full aspect-video rounded-2xl border-2 border-dashed border-[#EAE7E0] hover:border-[#1E3A8A] bg-[#F3F1ED] flex flex-col items-center justify-center gap-1.5 text-[#4B5563] hover:text-[#1E3A8A] transition-colors">
          <ImgIcon size={22} />
          <div className="text-sm font-medium">Klik untuk mengunggah foto</div>
          <div className="text-[11px] text-[#4B5563]">JPEG / PNG / WebP · maks 8 MB · akan dipotong 16:9</div>
        </button>
      )}
      <input ref={inputRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={pickFile} data-testid={`${testidPrefix}-input`} />

      <Dialog open={open} onOpenChange={(v) => { if (!busy) { setOpen(v); if (!v) setRawSrc(null); } }}>
        <DialogContent className="max-w-2xl" data-testid={`${testidPrefix}-dialog`}>
          <DialogHeader><DialogTitle>Potong foto ke rasio {aspect === 16/9 ? "16:9" : aspect}</DialogTitle></DialogHeader>
          {rawSrc && (
            <div className="space-y-4">
              <div className="relative w-full h-[360px] bg-black rounded-xl overflow-hidden">
                <Cropper image={rawSrc} crop={crop} zoom={zoom} aspect={aspect}
                  onCropChange={setCrop} onZoomChange={setZoom} onCropComplete={onCropComplete}
                  restrictPosition showGrid />
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest text-[#4B5563] mb-1.5">Zoom</div>
                <Slider min={1} max={4} step={0.05} value={[zoom]} onValueChange={(v) => setZoom(v[0])} data-testid={`${testidPrefix}-zoom`} />
              </div>
              <div className="flex justify-end gap-2 pt-1">
                <Button variant="outline" onClick={() => setOpen(false)} disabled={busy} className="rounded-full h-10">Batal</Button>
                <Button onClick={cropAndUpload} disabled={busy || !croppedAreaPixels} data-testid={`${testidPrefix}-submit`}
                  className="rounded-full bg-[#1E3A8A] hover:bg-[#1E40AF] text-white h-10">
                  {busy ? "Mengunggah…" : "Potong & unggah"}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ---- helper: crop dataURL image via canvas → JPEG blob (q 0.85, max 1920 wide) ----
async function cropImageToBlob(dataUrl, area) {
  const img = await loadImage(dataUrl);
  const maxW = 1920;
  const scale = Math.min(1, maxW / area.width);
  const outW = Math.round(area.width * scale);
  const outH = Math.round(area.height * scale);
  const canvas = document.createElement("canvas");
  canvas.width = outW; canvas.height = outH;
  const ctx = canvas.getContext("2d");
  ctx.drawImage(img, area.x, area.y, area.width, area.height, 0, 0, outW, outH);
  return new Promise((resolve) => canvas.toBlob((b) => resolve(b), "image/jpeg", 0.85));
}

function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
}
